import { Editor, useEditorState } from '@tiptap/react';
import { FlexBox } from '@ardoq/layout';
import { ColorPickerToolbarButton } from './ColorPickerToolbarButton';
import { ToolbarButton } from './ToolbarButton';
import { LinkDropdown } from './LinkDropdown';
import { breakpoints, colors, radius } from '@ardoq/design-tokens';
import styled from 'styled-components';
import { ToolbarDropdown } from './ToolbarDropdown';
import {
  createToolbarActionItemToToolbarDropdownOption,
  createToolbarItemsConfig,
  defaultToolbarItemLabels,
  ToolbarItem,
  ToolbarItemDropdown,
  ToolbarItemLabels,
} from './toolbarItems';
import { IconName } from '@ardoq/icons';
import { hasExtensionEnabled, hasToolbarItemEnabled } from './toolbarUtils';
import { ReactNode, useMemo } from 'react';
import { LinkFormLabels } from './LinkForm';

export type ToolbarLabels = {
  moreElements: string;
  textFormatting: string;
  listFormatting: string;
  items: ToolbarItemLabels;
};

export const defaultToolbarLabels: ToolbarLabels = {
  moreElements: 'More elements',
  textFormatting: 'Text Formatting',
  listFormatting: 'List Formatting',
  items: defaultToolbarItemLabels,
};

type ToolbarProps = {
  editor: Editor | null;
  isDisabled?: boolean;
  width?: number;
  additionalToolbarButtons?: ReactNode;
  labels?: ToolbarLabels;
  linkFormLabels?: LinkFormLabels;
  linkTooltipText?: string;
};

export const ToolbarDivider = styled.div`
  background-color: ${colors.borderSubtle};
  width: 2px;
  height: 18px;
`;

type ToolbarContainerProps = {
  children: React.ReactNode;
  isDisabled?: boolean;
  isTruncated?: boolean;
};

const ToolbarContainer = ({
  children,
  isDisabled,
  isTruncated,
}: ToolbarContainerProps) => (
  <FlexBox
    gap={isTruncated ? 'xxsmall' : 'xsmall'}
    flexWrap={true}
    backgroundColor={isDisabled ? 'surfaceDisabled' : 'surfaceDefaultSubtle'}
    paddingX="xsmall"
    paddingY="xxsmall"
    align="center"
    style={{
      borderColor: colors.borderAction,
      borderTopLeftRadius: radius.medium,
      borderTopRightRadius: radius.medium,
      borderStyle: 'solid',
      borderWidth: '0px 0px 1px 0px',
    }}
  >
    {children}
  </FlexBox>
);

type ToolbarItemRendererProps = {
  name: string;
  isDisabled?: boolean;
  editor: Editor;
  itemsConfig: Record<string, ToolbarItem>;
  linkFormLabels?: LinkFormLabels;
  linkTooltipText?: string;
};

const ToolbarItemRenderer = ({
  name,
  isDisabled,
  editor,
  itemsConfig,
  linkFormLabels,
  linkTooltipText,
}: ToolbarItemRendererProps) => {
  const item = itemsConfig[name];
  const isActive = (isDisabled !== true && editor.isActive(name)) || false;
  if (item.type === 'dropdown') {
    return (
      <ToolbarDropdown
        editor={editor}
        tooltipText={item.tooltipText}
        hideChevron
        isDisabled={isDisabled || item.isDisabled?.(editor)}
        options={
          isActive && item.getActiveOptions
            ? item.getActiveOptions(editor)
            : item.getOptions(editor)
        }
        icon={item.icon}
      />
    );
  } else if (item.type === 'link-dropdown') {
    return (
      <LinkDropdown
        isDisabled={isDisabled}
        editor={editor}
        labels={linkFormLabels}
        tooltipText={linkTooltipText ?? item.tooltipText}
      />
    );
  } else if (item.type === 'color-picker') {
    const isActive =
      editor.getAttributes('textStyle').color && name === 'color';
    return (
      <ColorPickerToolbarButton
        editor={editor}
        icon={item.icon}
        isActive={isActive}
        resolveCurrentColor={item.resolveCurrentColor}
        tooltipText={item.tooltipText}
        isDisabled={isDisabled}
      />
    );
  }
  return (
    <ToolbarButton
      tooltipText={item.tooltipText}
      isDisabled={isDisabled}
      onClick={() => item.onClick(editor)}
      isActive={isActive}
      icon={item.icon}
      shortcuts={item.shortcut}
    />
  );
};

const ToolbarItemsRenderer = ({
  itemNames,
  isDisabled,
  editor,
  itemsConfig,
  linkFormLabels,
  linkTooltipText,
}: {
  itemNames: string[];
  isDisabled?: boolean;
  editor: Editor;
  itemsConfig: Record<string, ToolbarItem>;
  linkFormLabels?: LinkFormLabels;
  linkTooltipText?: string;
}) => {
  return (
    <>
      {itemNames.map(itemName => (
        <ToolbarItemRenderer
          key={itemName}
          name={itemName}
          isDisabled={isDisabled}
          editor={editor}
          itemsConfig={itemsConfig}
          linkFormLabels={linkFormLabels}
          linkTooltipText={linkTooltipText}
        />
      ))}
    </>
  );
};

type CommonBucketProps = {
  editor: Editor;
  isDisabled?: boolean;
  itemsConfig: Record<string, ToolbarItem>;
  labels: ToolbarLabels;
  linkFormLabels?: LinkFormLabels;
  linkTooltipText?: string;
};

type BlocksBucketProps = CommonBucketProps & {
  maxItems?: number;
};

const BlocksBucket = ({
  editor,
  isDisabled,
  maxItems = 7,
  itemsConfig,
  labels,
  linkFormLabels,
  linkTooltipText,
}: BlocksBucketProps) => {
  const blockExtensions = [
    'table',
    'link',
    'image',
    'internalLink',
    'horizontalRule',
    'codeBlock',
    'blockquote',
  ];

  if (maxItems < 2) {
    throw new Error(
      'maxItems must be greater than or equal to 2 since the table and link extensions are dropdowns'
    );
  }
  const enabledBlockExtensions = blockExtensions.filter(extensionName =>
    hasToolbarItemEnabled(editor, extensionName)
  );

  if (enabledBlockExtensions.length === 0) {
    return null;
  }

  if (enabledBlockExtensions.length <= maxItems) {
    return (
      <>
        <ToolbarDivider />
        <ToolbarItemsRenderer
          itemNames={enabledBlockExtensions}
          isDisabled={isDisabled}
          editor={editor}
          itemsConfig={itemsConfig}
          linkFormLabels={linkFormLabels}
          linkTooltipText={linkTooltipText}
        />
      </>
    );
  }

  const limitedBlockExtensions = maxItems
    ? enabledBlockExtensions.slice(0, maxItems)
    : enabledBlockExtensions;

  const toToolbarDropdownOption =
    createToolbarActionItemToToolbarDropdownOption(itemsConfig);

  return (
    <>
      <ToolbarDivider />
      <ToolbarItemsRenderer
        itemNames={limitedBlockExtensions}
        isDisabled={isDisabled}
        editor={editor}
        itemsConfig={itemsConfig}
        linkFormLabels={linkFormLabels}
        linkTooltipText={linkTooltipText}
      />
      <ToolbarDropdown
        editor={editor}
        tooltipText={labels.moreElements}
        icon={{
          name: IconName.MORE_HORIZ,
        }}
        isActive={enabledBlockExtensions
          .filter(extension => !limitedBlockExtensions.includes(extension))
          .some(extension => editor.isActive(extension))}
        options={enabledBlockExtensions
          .filter(extension => !limitedBlockExtensions.includes(extension))
          .map(toToolbarDropdownOption)}
        isDisabled={isDisabled}
        hideChevron
      />
    </>
  );
};

const ParagraphBucket = ({
  editor,
  isDisabled,
  itemsConfig,
  labels,
}: CommonBucketProps) => {
  const item = itemsConfig.paragraph as ToolbarItemDropdown;
  const options = item.getOptions(editor);
  return (
    <>
      <ToolbarDropdown
        editor={editor}
        tooltipText={item.tooltipText}
        hideChevron
        icon={item.icon}
        isActive={options.some(
          option =>
            option.isActive &&
            option.isActive(editor) &&
            option.label !== labels.items.normal
        )}
        options={options}
        isDisabled={isDisabled}
      />
    </>
  );
};

const FormattingBucket = ({
  editor,
  isDisabled,
  isTruncated = false,
  itemsConfig,
  labels,
  linkFormLabels,
  linkTooltipText,
}: ListBucketProps) => {
  const formattingExtensions = ['bold', 'italic', 'strike', 'code'];
  const enabledFormattingExtensions = formattingExtensions.filter(
    extensionName => hasExtensionEnabled(editor, extensionName)
  );

  if (enabledFormattingExtensions.length === 0) {
    return null;
  }

  if (isTruncated) {
    const [boldExtension, ...remainingExtensions] = enabledFormattingExtensions;
    const toToolbarDropdownOption =
      createToolbarActionItemToToolbarDropdownOption(itemsConfig);
    return (
      <>
        <ToolbarDivider />
        <ToolbarItemRenderer
          name={boldExtension}
          editor={editor}
          isDisabled={isDisabled}
          itemsConfig={itemsConfig}
          linkFormLabels={linkFormLabels}
          linkTooltipText={linkTooltipText}
        />
        {enabledFormattingExtensions.length > 1 && (
          <ToolbarDropdown
            editor={editor}
            tooltipText={labels.textFormatting}
            icon={{
              name: IconName.MORE_HORIZ,
            }}
            isActive={remainingExtensions.some(extension =>
              editor.isActive(extension)
            )}
            isDisabled={isDisabled}
            hideChevron
            options={remainingExtensions.map(toToolbarDropdownOption)}
          />
        )}
      </>
    );
  }
  return (
    <>
      <ToolbarDivider />
      <ToolbarItemsRenderer
        itemNames={enabledFormattingExtensions}
        isDisabled={isDisabled}
        editor={editor}
        itemsConfig={itemsConfig}
        linkFormLabels={linkFormLabels}
        linkTooltipText={linkTooltipText}
      />
    </>
  );
};

const ColorBucket = ({
  editor,
  isDisabled,
  itemsConfig,
  linkFormLabels,
  linkTooltipText,
}: CommonBucketProps) => {
  const colorExtensions = ['color'];
  const enabledColorExtensions = colorExtensions.filter(extensionName =>
    hasExtensionEnabled(editor, extensionName)
  );
  if (enabledColorExtensions.length === 0) {
    return null;
  }

  return (
    <>
      <ToolbarDivider />
      <ToolbarItemsRenderer
        itemNames={enabledColorExtensions}
        isDisabled={isDisabled}
        editor={editor}
        itemsConfig={itemsConfig}
        linkFormLabels={linkFormLabels}
        linkTooltipText={linkTooltipText}
      />
    </>
  );
};

type ListBucketProps = CommonBucketProps & {
  isTruncated?: boolean;
};

const ListBucket = ({
  editor,
  isDisabled,
  isTruncated = false,
  itemsConfig,
  labels,
  linkFormLabels,
  linkTooltipText,
}: ListBucketProps) => {
  const listExtensions = ['bulletList', 'orderedList'];
  const enabledListExtensions = listExtensions.filter(extensionName =>
    hasExtensionEnabled(editor, extensionName)
  );
  if (enabledListExtensions.length === 0) {
    return null;
  }

  const toToolbarDropdownOption =
    createToolbarActionItemToToolbarDropdownOption(itemsConfig);

  return (
    <>
      <ToolbarDivider />
      {isTruncated ? (
        <ToolbarDropdown
          isActive={enabledListExtensions.some(extension =>
            editor.isActive(extension)
          )}
          editor={editor}
          tooltipText={labels.listFormatting}
          icon={{
            name: IconName.FORMAT_LIST_BULLETED,
          }}
          isDisabled={isDisabled}
          options={enabledListExtensions.map(toToolbarDropdownOption)}
        />
      ) : (
        <ToolbarItemsRenderer
          itemNames={enabledListExtensions}
          editor={editor}
          isDisabled={isDisabled}
          itemsConfig={itemsConfig}
          linkFormLabels={linkFormLabels}
          linkTooltipText={linkTooltipText}
        />
      )}
    </>
  );
};

/**
 * These extensions are currently only used in Broadcast. So add them in
 * a separate bucket make rendering the other blocks easier.
 */
const BroadcastBucket = ({
  editor,
  isDisabled,
  itemsConfig,
  linkFormLabels,
  linkTooltipText,
}: CommonBucketProps) => {
  const broadcastExtensions = ['presentation', 'placeholderDropdown'];
  const enabledBroadcastExtensions = broadcastExtensions.filter(extensionName =>
    hasExtensionEnabled(editor, extensionName)
  );
  if (enabledBroadcastExtensions.length === 0) {
    return null;
  }
  return (
    <>
      <ToolbarDivider />
      <ToolbarItemsRenderer
        itemNames={enabledBroadcastExtensions}
        editor={editor}
        isDisabled={isDisabled}
        itemsConfig={itemsConfig}
        linkFormLabels={linkFormLabels}
        linkTooltipText={linkTooltipText}
      />
    </>
  );
};

const AdditionalToolbarButtonsBucket = ({
  additionalToolbarButtons,
}: {
  additionalToolbarButtons: ReactNode;
}) => {
  if (!additionalToolbarButtons) {
    return null;
  }
  return (
    <>
      <ToolbarDivider />
      {additionalToolbarButtons}
    </>
  );
};

const HistoryBucket = ({
  editor,
  isDisabled,
  itemsConfig,
  linkFormLabels,
  linkTooltipText,
}: CommonBucketProps) => {
  return (
    <>
      <ToolbarDivider />
      <ToolbarItemRenderer
        name="undo"
        isDisabled={isDisabled}
        editor={editor}
        itemsConfig={itemsConfig}
        linkFormLabels={linkFormLabels}
        linkTooltipText={linkTooltipText}
      />
      <ToolbarItemRenderer
        name="redo"
        isDisabled={isDisabled}
        editor={editor}
        itemsConfig={itemsConfig}
        linkFormLabels={linkFormLabels}
        linkTooltipText={linkTooltipText}
      />
    </>
  );
};

export const Toolbar = ({
  editor,
  isDisabled,
  width,
  additionalToolbarButtons,
  labels = defaultToolbarLabels,
  linkFormLabels,
  linkTooltipText,
}: ToolbarProps) => {
  // Subscribe to editor state changes so toolbar buttons reflect active
  // marks/nodes. In tiptap v3 shouldRerenderOnTransaction defaults to false,
  // so this is needed to keep the toolbar in sync.
  useEditorState({
    editor,
    selector: ctx => ctx.editor?.state.selection,
  });

  const itemsConfig = useMemo(
    () => createToolbarItemsConfig(labels.items),
    [labels.items]
  );

  if (!editor) {
    return <></>;
  }
  const commonBucketProps = {
    editor,
    isDisabled,
    itemsConfig,
    labels,
    linkFormLabels,
    linkTooltipText,
  };

  if (width && width < Number.parseInt(breakpoints.tablet, 10)) {
    return (
      <ToolbarContainer isDisabled={isDisabled} isTruncated>
        <ParagraphBucket {...commonBucketProps} />
        <FormattingBucket {...commonBucketProps} isTruncated />
        <ColorBucket {...commonBucketProps} />
        <ListBucket {...commonBucketProps} isTruncated />
        <BroadcastBucket {...commonBucketProps} />
        <BlocksBucket {...commonBucketProps} maxItems={2} />
        <AdditionalToolbarButtonsBucket
          additionalToolbarButtons={additionalToolbarButtons}
        />
      </ToolbarContainer>
    );
  }

  return (
    <ToolbarContainer isDisabled={isDisabled}>
      <ParagraphBucket {...commonBucketProps} />
      <FormattingBucket {...commonBucketProps} />
      <ColorBucket {...commonBucketProps} />
      <ListBucket {...commonBucketProps} />
      <BroadcastBucket {...commonBucketProps} />
      <BlocksBucket {...commonBucketProps} />
      <AdditionalToolbarButtonsBucket
        additionalToolbarButtons={additionalToolbarButtons}
      />
      <HistoryBucket {...commonBucketProps} />
    </ToolbarContainer>
  );
};
